import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const KLAVIYO_REVISION = '2024-10-15';
const LOCAL_DEV_COMPANY_ID = 'LOCAL1';
const STORAGE_PREFIX = 'bis:';

/**
 * @typedef {object} BackInStockRefs
 * @property {HTMLElement} initialState
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} submittingPill
 * @property {HTMLElement} [submittingLabel]
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} [subCopy]
 * @property {HTMLElement} doneState
 * @property {HTMLElement} [switchLoading]
 *
 * @extends {Component<BackInStockRefs>}
 */
class BackInStockComponent extends Component {
  requiredRefs = ['initialState', 'form', 'emailInput', 'submitButton', 'submittingPill', 'errorMessage', 'doneState'];

  /** @type {AbortController | null} */
  #variantEventAbort = null;

  /** @type {boolean} */
  #submitting = false;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #idleButtonWidth = null;

  connectedCallback() {
    super.connectedCallback();
    this.#restoreFromStorage();
    this.#bindVariantEvents();
  }

  disconnectedCallback() {
    this.#abortInFlight();
    this.#variantEventAbort?.abort();
    this.#variantEventAbort = null;
    super.disconnectedCallback();
  }

  #bindVariantEvents() {
    this.#variantEventAbort?.abort();
    this.#variantEventAbort = new AbortController();
    const { signal } = this.#variantEventAbort;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  #onVariantSelected = () => {
    const wrap = this.closest('[data-bis-wrap]');
    if (wrap && !wrap.hasAttribute('hidden-soft')) {
      this.#setSwitchLoading(true);
    }
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    this.#setSwitchLoading(false);

    const detail = /** @type {{ resource?: Record<string, any>, data?: { html?: Document | Element, newProduct?: { title?: string } } }} */ (
      /** @type {{ detail?: any }} */ (event).detail || {}
    );
    const resource = detail.resource;
    if (!resource?.id) return;

    const html = detail.data?.html;
    const fromHtml =
      html instanceof Document || html instanceof Element
        ? html.querySelector('back-in-stock-component')
        : null;

    this.updateVariant(resource, {
      productTitle: detail.data?.newProduct?.title,
      variantTitle:
        fromHtml instanceof HTMLElement
          ? fromHtml.dataset.variantTitle
          : this.#selectedOptionLabelFromDom(),
    });
  };

  /**
   * @param {boolean} isLoading
   */
  #setSwitchLoading(isLoading) {
    const { switchLoading } = this.refs;
    if (!(switchLoading instanceof HTMLElement)) return;

    switchLoading.hidden = !isLoading;
    switchLoading.setAttribute('aria-hidden', String(!isLoading));
  }

  /**
   * @param {{ id?: string | number, title?: string, name?: string, public_title?: string, option1?: string, option2?: string, option3?: string, options?: string[], available?: boolean }} variant
   * @param {{ productTitle?: string, variantTitle?: string }} [options]
   */
  updateVariant(variant, options = {}) {
    if (!variant?.id) return;

    const variantId = String(variant.id);
    this.dataset.variantId = variantId;
    this.dataset.variantCatalogId = `$shopify:::$default:::${variantId}`;
    this.dataset.variantTitle = this.#resolveVariantTitle(variant, options);

    this.reset();
  }

  /**
   * Prefer the currently checked size/option label in the picker UI.
   * @returns {string}
   */
  #selectedOptionLabelFromDom() {
    const scope = this.closest('.shopify-section, dialog, product-card, .quick-add-modal__content') || document;
    const checked = /** @type {HTMLInputElement | null} */ (
      scope.querySelector(
        'variant-picker input[type="radio"]:checked, .variant-picker input[type="radio"]:checked'
      )
    );
    if (!checked) return '';

    const label =
      checked.labels?.[0] ||
      (checked.id ? scope.querySelector(`label[for="${CSS.escape(checked.id)}"]`) : null) ||
      checked.closest('label');

    const text = (label?.textContent || checked.value || checked.dataset.optionValue || '').trim();
    return text.replace(/\s+/g, ' ');
  }

  /**
   * @param {{ title?: string, name?: string, public_title?: string, option1?: string, option2?: string, option3?: string, options?: string[] }} variant
   * @param {{ productTitle?: string, variantTitle?: string }} [options]
   * @returns {string}
   */
  #resolveVariantTitle(variant, options = {}) {
    const explicit = (options.variantTitle || '').trim();
    if (explicit && explicit !== 'Default Title') return explicit;

    const publicTitle = (variant.public_title || '').trim();
    if (publicTitle && publicTitle !== 'Default Title') return publicTitle;

    const title = (variant.title || '').trim();
    if (title && title !== 'Default Title') return title;

    const optionParts = [variant.option1, variant.option2, variant.option3]
      .map((part) => (typeof part === 'string' ? part.trim() : ''))
      .filter(Boolean);
    if (optionParts.length === 1) return optionParts[0];
    if (optionParts.length > 1) return optionParts.join(' / ');

    const optionsArray = Array.isArray(variant.options)
      ? variant.options.map((part) => String(part || '').trim()).filter(Boolean)
      : [];
    if (optionsArray.length === 1) return optionsArray[0];
    if (optionsArray.length > 1) return optionsArray.join(' / ');

    const fromDom = this.#selectedOptionLabelFromDom();
    if (fromDom) return fromDom;

    return options.productTitle || this.dataset.productTitle || title;
  }

  /**
   * @returns {string}
   */
  #currentVariantTitle() {
    return (
      (this.dataset.variantTitle || '').trim() ||
      this.#selectedOptionLabelFromDom() ||
      (this.dataset.productTitle || '').trim()
    );
  }

  /**
   * Abort in-flight request and restore Initial or Completed for the current variant.
   */
  reset() {
    this.#abortInFlight();
    this.#setSwitchLoading(false);
    this.#setSubmitting(false);
    this.#clearError();
    this.#idleButtonWidth = null;

    if (this.#restoreFromStorage()) return;

    this.#showInitial();
  }

  /**
   * Klaviyo client endpoints expect the 6-character public Site ID.
   * Merchants sometimes paste the longer pk_{siteId}_… public key instead.
   * @param {string} companyId
   * @returns {string | null}
   */
  #normalizeCompanyId(companyId) {
    if (!companyId) return null;
    if (companyId === LOCAL_DEV_COMPANY_ID) return companyId;

    const trimmed = companyId.trim();
    if (/^[A-Za-z0-9]{6}$/.test(trimmed)) return trimmed;

    const pkMatch = trimmed.match(/^pk_([A-Za-z0-9]{6})_/);
    if (pkMatch) return pkMatch[1];

    return null;
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.#submitting) return;

    const email = this.refs.emailInput.value.trim();

    if (!this.#isValidEmail(email)) {
      this.#showError(this.dataset.invalidEmailMessage || "That email didn't look right — mind checking it?");
      this.refs.emailInput.focus();
      this.refs.emailInput.setAttribute('aria-invalid', 'true');
      return;
    }

    this.refs.emailInput.removeAttribute('aria-invalid');
    this.#clearError();

    const companyId = this.#normalizeCompanyId(this.dataset.companyId || '');
    const variantCatalogId = this.dataset.variantCatalogId;
    const variantId = this.dataset.variantId;

    if (!companyId || !variantCatalogId || !variantId) {
      this.#showError(
        this.dataset.configErrorMessage ||
          this.dataset.errorMessage ||
          'Something went wrong. Try again in a moment.'
      );
      return;
    }

    this.#setSubmitting(true);
    this.#abortController = new AbortController();

    try {
      if (companyId === LOCAL_DEV_COMPANY_ID) {
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(resolve, 400);
          this.#abortController?.signal.addEventListener(
            'abort',
            () => {
              clearTimeout(timeoutId);
              reject(new DOMException('Aborted', 'AbortError'));
            },
            { once: true }
          );
        });
        console.info('[back-in-stock] LOCAL1 filler ID — subscription not sent to Klaviyo.');
        this.#complete(email);
        return;
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION,
          },
          signal: this.#abortController.signal,
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                    },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: variantCatalogId,
                  },
                },
              },
            },
          }),
        }
      );

      if (response.ok || response.status === 202) {
        this.#complete(email);
        return;
      }

      this.#showError(this.dataset.errorMessage || 'Something went wrong. Try again in a moment.');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      const isNetworkFailure = error instanceof TypeError;
      this.#showError(
        isNetworkFailure
          ? this.dataset.networkErrorMessage ||
              'Could not reach Klaviyo. Check your connection or ad blocker, then try again.'
          : this.dataset.errorMessage || 'Something went wrong. Try again in a moment.'
      );
    } finally {
      if (this.#submitting) {
        this.#setSubmitting(false);
      }
      this.#abortController = null;
    }
  };

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * @param {boolean} isLoading
   */
  #setSubmitting(isLoading) {
    this.#submitting = isLoading;
    const { form, emailInput, submitButton, submittingPill, submittingLabel } = this.refs;

    form.setAttribute('aria-busy', String(isLoading));

    if (isLoading) {
      if (this.#idleButtonWidth == null) {
        this.#idleButtonWidth = Math.ceil(submitButton.getBoundingClientRect().width);
      }
      const width = this.#idleButtonWidth;
      submitButton.hidden = true;
      submittingPill.hidden = false;
      submittingPill.style.minWidth = `${width}px`;
      if (submittingLabel instanceof HTMLElement && this.dataset.submittingMessage) {
        submittingLabel.textContent = this.dataset.submittingMessage;
      }
      emailInput.readOnly = true;
    } else {
      submitButton.hidden = false;
      submittingPill.hidden = true;
      submittingPill.style.minWidth = '';
      emailInput.readOnly = false;
    }
  }

  /**
   * @param {string} email
   */
  #complete(email) {
    const variantId = this.dataset.variantId;
    const variantTitle = this.#currentVariantTitle();

    if (variantId) {
      try {
        localStorage.setItem(
          `${STORAGE_PREFIX}${variantId}`,
          JSON.stringify({ email, variantTitle })
        );
      } catch {
        // Ignore quota / private mode failures.
      }
    }

    this.#setSubmitting(false);
    this.#renderDone(email, variantTitle);
  }

  /**
   * @returns {boolean} true if completed state was restored
   */
  #restoreFromStorage() {
    const variantId = this.dataset.variantId;
    if (!variantId) return false;

    try {
      const raw = localStorage.getItem(`${STORAGE_PREFIX}${variantId}`);
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      if (!parsed?.email) return false;

      const storedTitle =
        typeof parsed.variantTitle === 'string' && parsed.variantTitle.trim()
          ? parsed.variantTitle.trim()
          : this.#currentVariantTitle();
      this.#renderDone(String(parsed.email), storedTitle);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Decode residual HTML entities from Liquid attribute output (avoids You&#39;re).
   * @param {string} value
   * @returns {string}
   */
  #decodeEntities(value) {
    if (!value || !value.includes('&')) return value;

    return value
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  /**
   * @param {string} email
   * @param {string} variantTitle
   */
  #renderDone(email, variantTitle) {
    const { initialState, doneState } = this.refs;
    const headingText = this.#decodeEntities(
      this.dataset.successHeading || "You're on the list."
    );
    const bodyTemplate = this.#decodeEntities(
      this.dataset.successBody || "We'll email {email} the moment {size} is back in stock."
    );

    initialState.hidden = true;
    doneState.hidden = false;
    doneState.replaceChildren();

    const icon = document.createElement('img');
    icon.src = this.dataset.checkIconUrl || '';
    icon.width = 24;
    icon.height = 24;
    icon.alt = '';

    const copy = document.createElement('div');
    const heading = document.createElement('h2');
    heading.textContent = headingText;

    const body = document.createElement('p');
    this.#appendInterpolatedBody(body, bodyTemplate, email, variantTitle);

    copy.append(heading, body);
    doneState.append(icon, copy);
  }

  /**
   * Build success body with <strong> around email and size without using innerHTML.
   * @param {HTMLElement} container
   * @param {string} template
   * @param {string} email
   * @param {string} size
   */
  #appendInterpolatedBody(container, template, email, size) {
    const parts = template.split(/(\{email\}|\{size\})/g);

    for (const part of parts) {
      if (part === '{email}') {
        const strong = document.createElement('strong');
        strong.textContent = email;
        container.append(strong);
      } else if (part === '{size}') {
        const strong = document.createElement('strong');
        strong.textContent = size;
        container.append(strong);
      } else if (part) {
        container.append(document.createTextNode(part));
      }
    }
  }

  #showInitial() {
    const { initialState, doneState, emailInput } = this.refs;
    initialState.hidden = false;
    doneState.hidden = true;
    doneState.replaceChildren();
    emailInput.readOnly = false;
    this.#clearError();
  }

  /**
   * @param {string} text
   */
  #showError(text) {
    const { errorMessage, emailInput } = this.refs;
    errorMessage.hidden = false;
    errorMessage.textContent = text;
    emailInput.setAttribute('aria-invalid', 'true');
  }

  #clearError() {
    const { errorMessage, emailInput } = this.refs;
    errorMessage.hidden = true;
    errorMessage.textContent = '';
    emailInput.removeAttribute('aria-invalid');
  }

  #abortInFlight() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    this.#submitting = false;
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
